import { authSelectors } from 'ducks/auth'
import { orgSelectors } from 'ducks/orgs'
import IndicativePriceLabel from 'elements/IndicativePriceLabel'
import PriceOnApplicationPricingLabel from 'elements/PriceOnApplicationPricingLabel'
import TieredPriceOnApplicationPricingLabel from 'elements/TieredPriceOnApplicationPricingLabel'
import { HardwareSupplierFilterKeyType } from 'opensolar-common'
import { BaseTheme, Box, Typography } from 'opensolar-ui'
import DealBadge from 'pages/ordering/deals/DealBadge'
import { computeDealPrice, filterProductLevelDeals } from 'pages/ordering/deals/dealEngine'
import { useGetSupplierConfig } from 'pages/ordering/hooks/useGetSupplierConfig'
import useIsIndicativePricing from 'pages/ordering/hooks/useIsIndicativePricing'
import useIsPriceOnApplication from 'pages/ordering/hooks/useIsPriceOnApplication'
import useIsTieredPriceOnApplication from 'pages/ordering/hooks/useIsTieredPriceOnApplication'
import { ComponentCost } from 'pages/ordering/type'
import React, { useCallback, useContext, useMemo } from 'react'
import { useTranslate } from 'react-admin'
import { useSelector } from 'react-redux'
import { ComponentTypesV2 } from 'types/selectComponent'
import { useFeatureFlag } from 'util/featureFlags'
import { currencySymbolForCountry, formatDecimalPlaces, mapComponentTypesV2ToV1 } from 'util/misc'
import { DistributorTradePricingContext, DistributorTradePricingContextType } from '../HardwareSelectorCore'
import { DistributorDataTypeV2, HardwareComponentDataType } from '../types'
import EmptyColumnItem from './EmptyColumnItem'
import useCommonStyles from './useCommonStyles'
import { formatPricePerRatedUnit, getComponentRating } from './utility'

interface PriceBlockInterface {
  code: string
  combinedRating?: number
  componentData?: HardwareComponentDataType
  componentType: ComponentTypesV2
  distributors: DistributorDataTypeV2[]
  costSource?: ComponentCost | null
}

const PricingSource = ({ source }: { source: ComponentCost | null | undefined }) => {
  const { getSupplierConfig } = useGetSupplierConfig()
  const translate = useTranslate()

  if (source?.source === 'component') return <Typography>{translate('from Manual Pricing')}</Typography>

  return <Typography>{`from ${getSupplierConfig(source?.source as HardwareSupplierFilterKeyType)?.name}`}</Typography>
}

const PriceBlock: React.FC<PriceBlockInterface> = ({
  code,
  combinedRating,
  componentData,
  componentType,
  distributors,
  costSource,
}) => {
  const commonClasses = useCommonStyles()

  const countryIso2 = useSelector(orgSelectors.getOrgIso2)
  const currencySymbol = useMemo(() => currencySymbolForCountry(countryIso2), [countryIso2])
  const enabledDistributors = useSelector(orgSelectors.getEnabledHardwareSuppliers)
  const orgId = useSelector(authSelectors.getOrgId)
  const distributorTradePricing =
    useContext<DistributorTradePricingContextType>(DistributorTradePricingContext).distributorTradePricing
  const componentActivation = window.AccountHelper.getComponentActivationFromCode(
    code,
    mapComponentTypesV2ToV1(componentType)
  )
  const multiDistributor = useFeatureFlag('design_multi_distributor', 'on')

  const deals = useMemo(() => {
    const allDeals = distributors
      .filter((d) => enabledDistributors.includes(d.distributor))
      .map((d) => d.deals)
      .filter(Boolean)
      .flat()
    return filterProductLevelDeals(allDeals)
  }, [distributors, enabledDistributors])

  const getDealPrice = useCallback(
    (originalPrice: number, source: HardwareSupplierFilterKeyType) => {
      const filteredDeals = deals.filter((d) => d.distributor === source)
      const discountedPrice = computeDealPrice(originalPrice, filteredDeals)
      if (discountedPrice >= originalPrice) return null
      return discountedPrice
    },
    [deals]
  )

  const prices = useMemo(() => {
    if (costSource) return [costSource.price || 0]

    const hasComponentActivationCost = multiDistributor && componentActivation && componentActivation.cost > 0
    const isNotEnabledDistributor = (distributor: HardwareSupplierFilterKeyType) =>
      !enabledDistributors.includes(distributor)

    return distributors
      .reduce((lineItems, { distributor, price }) => {
        if (hasComponentActivationCost) lineItems.push(componentActivation.cost)
        if (isNotEnabledDistributor(distributor)) return lineItems

        const dealPrice = getDealPrice(price, distributor)
        return lineItems.includes(dealPrice ?? price)
          ? lineItems
          : [...lineItems, dealPrice ?? price].sort((a, b) => a - b)
      }, [] as number[])
      .filter(Boolean)
  }, [
    distributors,
    enabledDistributors,
    orgId,
    costSource,
    distributorTradePricing,
    componentActivation,
    multiDistributor,
    getDealPrice,
  ])

  const pricePerWatt = useMemo(() => {
    if ((componentType !== 'module' && !combinedRating) || prices.length === 0) return null

    const componentRating = combinedRating
      ? { suffix: 'kW', value: combinedRating }
      : getComponentRating(componentType, componentData)
    if (componentRating === '-' || typeof componentRating.value !== 'number') return null

    const rating = { suffix: componentRating.suffix, value: componentRating.value }
    const effectivePrice =
      costSource?.source && costSource.source !== 'component'
        ? (getDealPrice(prices[0], costSource.source as HardwareSupplierFilterKeyType) ?? prices[0])
        : Math.min(...prices)

    const label = formatPricePerRatedUnit(effectivePrice, rating, countryIso2, currencySymbol)
    return <span className={commonClasses.lightText}>{label}</span>
  }, [
    combinedRating,
    componentData,
    componentType,
    costSource,
    countryIso2,
    currencySymbol,
    getDealPrice,
    prices,
    commonClasses.lightText,
  ])

  const showDealPrice = useMemo(() => {
    if (!costSource || prices.length === 0) return false
    if (prices.length === 1) return (getDealPrice(prices[0], costSource.source) ?? prices[0]) !== prices[0]
    return false
  }, [prices, getDealPrice, costSource])

  const selectedSupplier =
    costSource?.source && costSource.source !== 'component'
      ? (costSource.source as HardwareSupplierFilterKeyType)
      : undefined
  const isIndicativePricing = useIsIndicativePricing(selectedSupplier)
  const isPriceOnApplication = useIsPriceOnApplication(selectedSupplier)
  const isTieredPriceOnApplication = useIsTieredPriceOnApplication(selectedSupplier, costSource?.price)

  if (isTieredPriceOnApplication)
    return <TieredPriceOnApplicationPricingLabel supplierFilterKey={selectedSupplier} variant="small" />
  if (isPriceOnApplication) return <PriceOnApplicationPricingLabel variant="small" />

  if (prices.length === 0) return <EmptyColumnItem />

  // If the source is manual pricing only and the price is 0, show an empty column item
  if (multiDistributor && costSource?.source === 'component' && prices[0] === 0 && prices.length === 1)
    return <EmptyColumnItem />

  if (costSource?.price === 0) return <EmptyColumnItem />

  return (
    <Box className={commonClasses.textsContainer} display="flex" flexDirection="column">
      {prices.length > 0 && (
        <>
          {showDealPrice ? (
            <Box display="flex" alignItems="center" style={{ gap: 6 }}>
              <Typography style={{ fontWeight: 600, color: BaseTheme.palette.red[800], fontSize: '14px' }}>
                {`${currencySymbol}${formatDecimalPlaces(
                  getDealPrice(prices[0], costSource?.source ?? '') ?? prices[0],
                  2
                )}`}
              </Typography>
              <Typography
                style={{
                  textDecoration: 'line-through',
                  color: BaseTheme.palette.grey[700],
                  fontSize: '12px',
                  fontWeight: 400,
                }}
              >
                {`${currencySymbol}${formatDecimalPlaces(prices[0], 2)}`}
              </Typography>
            </Box>
          ) : (
            <Typography className={commonClasses.mainText}>
              {`${isIndicativePricing ? '~' : ''}
            ${currencySymbol}${formatDecimalPlaces(prices[0], 2)}${
              prices.length > 1 ? `-${prices[prices.length - 1]}` : ''
            }`}
            </Typography>
          )}
        </>
      )}
      <DealBadge
        orderingV2={distributors}
        selectedDistributor={costSource?.source && costSource.source !== 'component' ? costSource.source : undefined}
        currentProductCode={code}
        showTooltip
        variant="light"
      />
      {pricePerWatt}
      {isIndicativePricing && <IndicativePriceLabel />}
    </Box>
  )
}

export default PriceBlock
